import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowUpFromLine,
  Eraser,
  Loader2,
  Replace,
  Tag,
  Trash2,
  UserMinus,
  UserPlus,
  X,
} from "lucide-react";
import type { ContactBulkGroupsMode, ContactGroupSummary } from "@multi-chat/shared";
import { Button } from "../../../components/ui/button/index.js";
import { AlertDialog, AlertDialogDescription, AlertDialogTitle } from "../../../components/ui/alert-dialog/index.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu/index.js";
import {
  contactsModalFooterCompactClass,
  contactsModalHeaderCompactClass,
} from "../contacts-dialog-presentation.js";
import { ContactsModalContent } from "../ContactsModalContent.js";
import { contactsBulkBarClass } from "../contacts-table-presentation.js";
import { ContactsBulkGroupDialog } from "./ContactsBulkGroupDialog.js";

export function ContactsBulkActionsBar({
  selectedCount,
  selectionMode,
  matchingTotal,
  orgActiveCount,
  excludedCount,
  showSelectMatching,
  showSelectAllActive,
  allowExport,
  groups,
  busy,
  exportBusy,
  onClearSelection,
  onSelectMatching,
  onSelectAllActive,
  onExportSelected,
  onDeleteSelected,
  onBulkGroups,
}: {
  selectedCount: number;
  selectionMode: "ids" | "matching_query" | "all_active";
  matchingTotal: number;
  orgActiveCount: number;
  excludedCount: number;
  showSelectMatching: boolean;
  showSelectAllActive: boolean;
  allowExport: boolean;
  groups: ContactGroupSummary[];
  busy: boolean;
  exportBusy: boolean;
  onClearSelection: () => void;
  onSelectMatching: () => void;
  onSelectAllActive: () => void;
  onExportSelected: () => Promise<void>;
  onDeleteSelected: () => Promise<void>;
  onBulkGroups: (mode: ContactBulkGroupsMode, selection: {
    groupIds: number[];
    newGroupNames: string[];
  }) => Promise<void>;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [groupDialog, setGroupDialog] = useState<ContactBulkGroupsMode | null>(null);
  const actionsDisabled = busy || exportBusy;

  if (selectedCount <= 0) {
    return null;
  }

  const semanticTotal =
    selectionMode === "matching_query"
      ? matchingTotal
      : selectionMode === "all_active"
        ? orgActiveCount
        : null;
  const countLabel =
    semanticTotal != null && excludedCount > 0
      ? t("contacts.selection.partialSelected", {
          selected: selectedCount,
          total: semanticTotal,
        })
      : selectionMode === "matching_query"
        ? t("contacts.selection.matchingSelected", { count: matchingTotal })
        : selectionMode === "all_active"
          ? t("contacts.selection.activeSelected", { count: orgActiveCount })
          : t("contacts.selection.pageSelected", { count: selectedCount });

  return (
    <>
      <div
        className={contactsBulkBarClass}
        role="region"
        aria-label={t("contacts.bulk.title")}
        data-testid="contacts-selection-banner"
        data-selection-mode={selectionMode}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="text-[0.8125rem] font-semibold text-foreground">{countLabel}</span>
          {showSelectMatching ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8"
              onClick={onSelectMatching}
              data-testid="contacts-select-matching"
            >
              {t("contacts.selection.selectMatching", { count: matchingTotal })}
            </Button>
          ) : null}
          {showSelectAllActive ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8"
              onClick={onSelectAllActive}
              data-testid="contacts-select-all-active"
            >
              {t("contacts.selection.selectActive", { count: orgActiveCount })}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8"
            onClick={onClearSelection}
            data-testid="contacts-clear-selection"
          >
            <X size={14} aria-hidden="true" />
            {t("contacts.bulk.clearSelection")}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
            {allowExport ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={actionsDisabled}
              onClick={() => void onExportSelected()}
            >
              {exportBusy ? (
                <Loader2 size={14} className="animate-spin" aria-hidden="true" />
              ) : (
                <ArrowUpFromLine size={14} aria-hidden="true" />
              )}
              {exportBusy ? t("contacts.actions.exporting") : t("contacts.bulk.export")}
            </Button>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={actionsDisabled}
                  data-testid="contacts-bulk-groups"
                >
                  <Tag size={14} aria-hidden="true" />
                  {t("contacts.bulk.groups")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="min-w-[12.5rem] p-1"
                data-testid="contacts-bulk-groups-menu"
              >
                <DropdownMenuItem
                  className="gap-2 px-2 py-1.5 text-[0.8125rem] font-medium"
                  onSelect={() => setGroupDialog("add")}
                  data-testid="contacts-bulk-groups-add"
                >
                  <UserPlus size={14} className="shrink-0 text-muted" aria-hidden="true" />
                  {t("contacts.bulk.addToGroup")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-2 px-2 py-1.5 text-[0.8125rem] font-medium"
                  onSelect={() => setGroupDialog("remove")}
                  data-testid="contacts-bulk-groups-remove"
                >
                  <UserMinus size={14} className="shrink-0 text-muted" aria-hidden="true" />
                  {t("contacts.bulk.removeFromGroup")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-2 px-2 py-1.5 text-[0.8125rem] font-medium"
                  onSelect={() => setGroupDialog("replace")}
                  data-testid="contacts-bulk-groups-replace"
                >
                  <Replace size={14} className="shrink-0 text-muted" aria-hidden="true" />
                  {t("contacts.bulk.replaceGroups")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-2 px-2 py-1.5 text-[0.8125rem] font-medium text-destructive data-[highlighted]:bg-error-muted"
                  onSelect={() => setGroupDialog("clear")}
                  data-testid="contacts-bulk-groups-clear"
                >
                  <Eraser size={14} className="shrink-0" aria-hidden="true" />
                  {t("contacts.bulk.clearGroups")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={actionsDisabled}
              onClick={() => setConfirmDelete(true)}
              data-testid="contacts-bulk-delete"
            >
              <Trash2 size={14} aria-hidden="true" />
              {t("contacts.bulk.delete")}
            </Button>
          </div>
      </div>

      <ContactsBulkGroupDialog
        open={groupDialog != null}
        mode={groupDialog ?? "add"}
        groups={groups}
        selectedCount={selectedCount}
        busy={busy}
        onClose={() => setGroupDialog(null)}
        onConfirm={async (selection) => {
          if (groupDialog == null) return;
          await onBulkGroups(groupDialog, selection);
          setGroupDialog(null);
        }}
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <ContactsModalContent>
          <div className={contactsModalHeaderCompactClass}>
            <AlertDialogTitle className="text-[0.9375rem] font-semibold tracking-tight">
              {t(
                selectionMode === "matching_query"
                  ? "contacts.bulk.deleteConfirmTitleMatching"
                  : selectionMode === "all_active"
                    ? "contacts.bulk.deleteConfirmTitleActive"
                    : "contacts.bulk.deleteConfirmTitleIds",
                { count: selectedCount },
              )}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[0.8125rem] leading-snug">
              {selectionMode === "all_active"
                ? excludedCount > 0
                  ? t("contacts.bulk.deleteConfirmScopeActivePartial", {
                      selected: selectedCount,
                      total: orgActiveCount,
                    })
                  : t("contacts.bulk.deleteConfirmScopeActive", { count: orgActiveCount })
                : null}
              {selectionMode === "all_active" ? " " : null}
              {t("contacts.bulk.deleteConfirmBody")}
            </AlertDialogDescription>
          </div>
          <div className={contactsModalFooterCompactClass}>
            <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={busy}
              data-testid="contacts-bulk-delete-confirm"
              onClick={() => {
                void onDeleteSelected().finally(() => setConfirmDelete(false));
              }}
            >
              {t("contacts.bulk.delete")}
            </Button>
          </div>
        </ContactsModalContent>
      </AlertDialog>
    </>
  );
}
